/**
 * Settings — where the settings of the books and the account live.
 *
 * REACHED FROM THE PROFILE MENU, NOT THE TOP BAR. The operator's decision,
 * 2026-09-13. The bar holds the screens used every day; a lock date is set a
 * few times a year. Later account and business settings belong here too, so
 * the page is sections under one heading rather than a screen named after its
 * first setting.
 *
 * BOOKS: CLOSING AND REOPENING
 *
 * Since migration 0027 the lock date is the ONLY thing that makes a posted
 * entry fixed. Everything dated on or before it -- the entry, its lines, its
 * receipts -- is refused change at the database. So closing is the heaviest
 * setting in the application, and the screen is built around one rule: the
 * operator sees what a date would do before they can apply it.
 *
 * Picking a date asks the server for a preview and writes nothing. The preview
 * counts what would become fixed and, more importantly, the drafts that would
 * be STRANDED: 0002's trigger refuses to post into a closed period, so an
 * uncoded bank transaction dated inside it could never enter the books. That
 * is a warning, not a refusal -- some of those drafts may be noise the operator
 * never means to post -- and it is said plainly rather than as a count in grey.
 *
 * A date that has not ended is refused by the server (`not_yet_ended`), with a
 * sentence naming the tenant's today. The date field's `max` is a convenience
 * derived from that same today; the server's refusal is the rule, and its
 * sentence is what is shown.
 *
 * REOPENING is allowed and recorded (0027), and gets an inline confirmation
 * that says what it does. It is not destructive, but it silently makes a filed
 * year editable, and that should never be one stray click.
 *
 * SIGNING IN (2026-09-20)
 *
 * Moving the authenticator to a new device, and reissuing recovery codes. Both
 * existed only as `cli/enroll-totp.ts` before, which needs a terminal -- so a
 * person whose phone was replaced had no way to move their second factor, and
 * a recovery sign-in started a ten-step countdown nothing could reset.
 *
 * THE COUNT IS SHOWN EVEN WHEN IT IS ZERO, which is the case it exists for: an
 * account whose next lost phone is a lockout. `remainingRecoveryCodes` was
 * computed, returned and discarded for months; this is the first screen to read
 * it.
 *
 * YOUR DATA (2026-09-20)
 *
 * The archive: everything this business holds, as one file they keep. It lives
 * here because it is an account setting rather than a daily screen, and because
 * the sentence that matters -- that this file cannot sign you in, so it is not
 * a way back into a locked-out account -- has to be read somewhere.
 *
 * PREPARING IS NOT DOWNLOADING and the screen says both. A build outlives its
 * request, so the button records a request and the list below is where the file
 * appears. A `stale` row is one nothing will finish: shown as abandoned, never
 * as "Preparing…", because a progress state that never resolves is how somebody
 * decides the feature is broken.
 *
 * BUSINESS DETAILS (issue #44, 2026-10-08)
 *
 * The EIN, legal name and address the filled Form 941 and the W-2 worksheet
 * carry. Its own component, components/BusinessDetailsSection.tsx, which says
 * the rest.
 *
 * PAYROLL: DRAFT OR POSTED (spec §18.6, 2026-09-14)
 *
 * Whether a payroll run's entry is posted or left a draft. Per business, by the
 * operator's choice, so it sits here beside the lock date rather than under a
 * person. It starts off -- drafts -- and the checkbox shows what the SERVER
 * holds after a change, not what was clicked, so a refused change leaves it as
 * it was. Read on its own, so a failure reading it leaves the books lock
 * working and is said in the Payroll section alone.
 */

import { useAuth, useBusiness, useUsesFunds } from '../auth/AuthProvider.js';
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { SettingsTab } from '../lib/settings-tabs.js';
import { BusinessDetailsSection } from '../components/BusinessDetailsSection.js';
import { FundsSection } from '../components/FundsSection.js';
import { PartnersSection } from '../components/PartnersSection.js';
import { ProjectsSection } from '../components/ProjectsSection.js';
import { LabelsSection } from '../components/LabelsSection.js';
import { PhasesSection } from '../components/PhasesSection.js';
import { QuickBooksImportSection } from '../components/QuickBooksImportSection.js';
import { LinkedSignInsSection } from '../components/LinkedSignInsSection.js';
import { BankConnectionsSection } from '../components/BankConnectionsSection.js';
import { BankImportSection } from '../components/BankImportSection.js';
import { PeopleSection } from '../components/PeopleSection.js';
import { CloseBusinessSection } from '../components/CloseBusinessSection.js';
import { InfoNote } from '../components/InfoNote.js';
import { Toast } from '../components/Toast.js';
import { RecoveryCodes } from '../components/RecoveryCodes.js';
import {
  ApiError,
  api,
  type BooksLock,
  type BooksLockChange,
  type BooksLockPreview,
  type BegunEnrolment,
  type DepositSchedule,
  type DepositScheduleEntry,
  type FiscalYears,
  type PayrollSettings,
  type SecurityState,
  type TenantExport,
  type TexasUnemploymentRate,
} from '../api.js';
import { parseRateMillionths } from '../lib/rates.js';
import { formatCents } from '../lib/money.js';
import {
  availableTimeZones,
  browserTimeZone,
  clearDisplayTimeZone,
  displayTimeZone,
  formatTimestamp,
  setDisplayTimeZone,
  useDisplayTimeZone,
} from '../lib/timezone.js';

const DATE = /^\d{4}-\d{2}-\d{2}$/;

/** The day before a YYYY-MM-DD date, computed in UTC so no timezone moves it. */
function dayBefore(date: string): string {
  const [year, month, day] = date.split('-').map(Number);
  return new Date(Date.UTC(year!, month! - 1, day! - 1)).toISOString().slice(0, 10);
}

/*
 * SETTINGS IN TABS (issue #66). The page had grown to a dozen sections read
 * down one column; the operator asked for a way around it. Tabs are the app's
 * existing pattern for "which part am I in" (Reports, the queue), and the
 * chosen tab is kept in the address as `?tab=`, so a link from elsewhere --
 * "Record the Deposit Schedule", a To Do row, the editor's "reopen it in
 * Settings" -- lands on the section it names rather than at the top.
 *
 * A tab is offered only when something in it is (0069): a viewer has no
 * Banks, Payroll or Your Data. An unknown or withheld `?tab=` opens Business
 * rather than an empty page. `lib/settings-tabs.ts` names the tabs.
 */

function count(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

export function SettingsPage() {
  // Each section only to whoever may use it (0069); the API refuses the rest either way.
  const { can } = useAuth();
  const usesFunds = useUsesFunds();
  // 0094: a business taxed with its owner closes into Owner's Equity and has draws, not distributions.
  const taxStatus = useBusiness()?.taxStatus;
  const taxedWithOwner = taxStatus === 'disregarded_entity';
  // 0096: a partnership closes to each partner's Capital, and keeps partners.
  const partnership = taxStatus === 'partnership';
  const mayExport = can('export_data');
  const mayClose = can('close_periods');
  const mayRunPayroll = can('run_payroll');
  const [searchParams, setSearchParams] = useSearchParams();
  const [lock, setLock] = useState<BooksLock | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [through, setThrough] = useState('');
  const [preview, setPreview] = useState<BooksLockPreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmingReopen, setConfirmingReopen] = useState(false);
  const [history, setHistory] = useState<BooksLockChange[] | null>(null);
  const [historyError, setHistoryError] = useState<string | null>(null);

  /* The year-end close (migration 0052). */
  const [fiscal, setFiscal] = useState<FiscalYears | null>(null);
  const [fiscalError, setFiscalError] = useState<string | null>(null);
  const [fiscalNotice, setFiscalNotice] = useState<string | null>(null);
  const [fiscalBusy, setFiscalBusy] = useState(false);
  const [reopeningYear, setReopeningYear] = useState<number | null>(null);
  const [reopenReason, setReopenReason] = useState('');

  const [payroll, setPayroll] = useState<PayrollSettings | null>(null);
  const [payrollError, setPayrollError] = useState<string | null>(null);
  const [payrollNotice, setPayrollNotice] = useState<string | null>(null);
  const [payrollBusy, setPayrollBusy] = useState(false);

  const [schedules, setSchedules] = useState<DepositScheduleEntry[] | null>(null);
  const [scheduleFrom, setScheduleFrom] = useState('');
  /** Empty until chosen: the schedule is asked for, never defaulted. */
  const [scheduleKind, setScheduleKind] = useState<DepositSchedule | ''>('');
  const [scheduleError, setScheduleError] = useState<string | null>(null);
  const [removingSchedule, setRemovingSchedule] = useState<string | null>(null);

  const [texasRates, setTexasRates] = useState<TexasUnemploymentRate[] | null>(null);
  const [texasYear, setTexasYear] = useState('');
  const [texasRate, setTexasRate] = useState('');
  const [texasError, setTexasError] = useState<string | null>(null);
  const [removingTexasYear, setRemovingTexasYear] = useState<number | null>(null);

  const [security, setSecurity] = useState<SecurityState | null>(null);
  const [securityError, setSecurityError] = useState<string | null>(null);
  const [securityBusy, setSecurityBusy] = useState(false);
  /** Which control is asking for a code, if any. */
  const [securityStep, setSecurityStep] = useState<'idle' | 'reissue' | 'enrol'>('idle');
  const [securityCode, setSecurityCode] = useState('');
  /** The factor being REPLACED, which proves the move is the owner's. */
  const [currentFactor, setCurrentFactor] = useState('');
  const [enrolment, setEnrolment] = useState<BegunEnrolment | null>(null);
  /** Shown once, then dropped. Never re-read from anywhere. */
  const [issuedCodes, setIssuedCodes] = useState<string[] | null>(null);
  const [issuedReason, setIssuedReason] = useState<'reissued' | 'enrolled'>('reissued');

  const [exports, setExports] = useState<TenantExport[] | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const [exportNotice, setExportNotice] = useState<string | null>(null);
  const [exportBusy, setExportBusy] = useState(false);

  const loadSecurity = useCallback(async (options: { clearError?: boolean } = {}) => {
    try {
      setSecurity(await api.readSecurity());
      if (options.clearError) setSecurityError(null);
    } catch (caught) {
      setSecurityError(
        caught instanceof ApiError ? caught.message : 'Could not read your sign-in settings.',
      );
    }
  }, []);

  useEffect(() => {
    void loadSecurity({ clearError: true });
  }, [loadSecurity]);

  /** Leaves every in-progress state behind. Used after a success and a cancel. */
  const resetSecurity = () => {
    setSecurityStep('idle');
    setSecurityCode('');
    setCurrentFactor('');
    setEnrolment(null);
  };

  const reissueCodes = async () => {
    setSecurityBusy(true);
    setSecurityError(null);
    try {
      const { codes } = await api.reissueRecoveryCodes(securityCode.trim());
      setIssuedReason('reissued');
      setIssuedCodes(codes);
      resetSecurity();
    } catch (caught) {
      setSecurityError(
        caught instanceof ApiError ? caught.message : 'Could not reissue your recovery codes.',
      );
    } finally {
      setSecurityBusy(false);
    }
  };

  const startEnrolment = async () => {
    setSecurityBusy(true);
    setSecurityError(null);
    try {
      setEnrolment(await api.beginTotpEnrolment());
      setSecurityStep('enrol');
      setSecurityCode('');
    } catch (caught) {
      setSecurityError(
        caught instanceof ApiError ? caught.message : 'Could not start the move.',
      );
    } finally {
      setSecurityBusy(false);
    }
  };

  const finishEnrolment = async () => {
    if (!enrolment) return;
    setSecurityBusy(true);
    setSecurityError(null);
    try {
      const { codes } = await api.confirmTotpEnrolment(
        enrolment.token,
        securityCode.trim(),
        currentFactor.trim(),
      );
      setIssuedReason('enrolled');
      setIssuedCodes(codes);
      resetSecurity();
    } catch (caught) {
      setSecurityError(
        caught instanceof ApiError ? caught.message : 'Could not finish the move.',
      );
    } finally {
      setSecurityBusy(false);
    }
  };

  /*
   * The archive list. Read on open and again after a request, so the row the
   * operator just caused is the first one shown.
   *
   * Read on its own and failing on its own, following the Payroll section: a
   * bucket that will not answer must not take the lock date down with it.
   */
  const loadExports = useCallback(async (options: { clearError?: boolean } = {}) => {
    try {
      const result = await api.listExports();
      setExports(result.exports);
      /*
       * A SUCCESSFUL READ DOES NOT CLEAR THE ERROR UNLESS ASKED.
       *
       * It used to, and that made both actions below silently swallow their own
       * refusal: each set the message and then refreshed the list, which wiped
       * it before a render. Pressing Prepare An Archive during another build
       * showed nothing at all -- the exact "I press it and nothing happens"
       * shape that cost a session on 2026-09-17, reintroduced by a refresh that
       * looked like housekeeping. Two tests in SettingsPage.test.tsx fail if the
       * unconditional clear comes back.
       */
      if (options.clearError) setExportError(null);
    } catch (caught) {
      setExportError(
        caught instanceof ApiError
          ? caught.message
          : 'Could not read the list of archives.',
      );
    }
  }, []);

  useEffect(() => {
    // Only for whoever sees the section (0069); anyone else would be refused.
    if (mayExport) void loadExports({ clearError: true });
  }, [loadExports, mayExport]);

  /*
   * A BUILD OUTLIVES ITS REQUEST, SO THE SCREEN HAS TO LOOK AGAIN.
   *
   * `prepareArchive` reads the list once, immediately -- at which point the row
   * is still `running`. So the screen said "Preparing..." and stayed there
   * until somebody reloaded the page by hand, while the archive had been ready
   * within a minute. Reported by the operator on 2026-09-20 against a build
   * that had finished. The header above already says "the list below is where
   * the file appears"; nothing made it appear.
   *
   * POLLS ONLY WHILE SOMETHING IS GENUINELY IN PROGRESS: `running` AND NOT
   * `stale`. A stale row is one nothing will finish -- the server restarted
   * mid-build -- so polling for it would never stop, which is the same
   * never-resolving progress state `describeExport` exists to avoid. When that
   * stops being true the interval is cleared, so a settled screen makes no
   * requests at all.
   */
  useEffect(() => {
    const building = exports?.some((archive) => archive.status === 'running' && !archive.stale);
    if (!building) return;

    const timer = window.setInterval(() => {
      void loadExports();
    }, EXPORT_POLL_MS);
    return () => window.clearInterval(timer);
  }, [exports, loadExports]);

  /*
   * THE BUTTON STAYS PRESSABLE and answers with a sentence.
   *
   * A build already under way answers 409 with what it is doing, which is the
   * useful reply -- a disabled button and a handler returning early say the
   * same nothing, and "I press it and nothing happens" cost a session once.
   */
  const prepareArchive = async () => {
    setExportBusy(true);
    setExportError(null);
    setExportNotice(null);
    try {
      await api.requestExport();
      setExportNotice(
        'Preparing your archive. It will appear below when it is ready — this can take a few minutes.',
      );
    } catch (caught) {
      setExportError(
        caught instanceof ApiError ? caught.message : 'Could not start the archive.',
      );
    } finally {
      setExportBusy(false);
      await loadExports();
    }
  };

  /*
   * The DOWNLOAD path, which is the one `window.open` is legitimate for: the
   * server signs the URL with `Content-Disposition: attachment`. A stored
   * document meant to be READ goes in ReceiptOverlay instead, and
   * `attachment-viewer.test.ts` fails any file that asks for `inline` and calls
   * this. An archive is a file to save, never one to view.
   */
  const downloadArchive = async (id: string) => {
    setExportError(null);
    try {
      const { url } = await api.exportDownloadUrl(id);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (caught) {
      setExportError(
        caught instanceof ApiError ? caught.message : 'Could not open that archive.',
      );
      // The list is refreshed because the usual reason a download fails is that
      // the file was pruned, and the row on screen still says otherwise.
      await loadExports();
    }
  };

  /*
   * §6.9 — every close and reopen, recorded by 0027's trigger and shown here.
   * Read on open and again after a change, so the row the operator just caused
   * is the first one listed.
   */
  const loadHistory = useCallback(async () => {
    try {
      const result = await api.getBooksLockHistory();
      setHistory(result.changes);
      setHistoryError(null);
    } catch (caught) {
      setHistoryError(caught instanceof ApiError ? caught.message : 'Could not read the history.');
    }
  }, []);

  useEffect(() => {
    // Only for whoever sees the section (0069); anyone else would be refused.
    if (mayClose) void loadHistory();
  }, [loadHistory, mayClose]);

  const loadFiscal = useCallback(async () => {
    try {
      setFiscal(await api.listFiscalYears());
      setFiscalError(null);
    } catch (caught) {
      setFiscalError(caught instanceof ApiError ? caught.message : 'Could not read the fiscal years.');
    }
  }, []);

  useEffect(() => {
    // Only for whoever sees the section (0069); anyone else would be refused.
    if (mayClose) void loadFiscal();
  }, [loadFiscal, mayClose]);

  /*
   * Close a year. The button is always pressable for a year that has ended
   * and is open: the server's refusal is a sentence, and the same sentence is
   * shown beside the row beforehand as `obstacle`, so nothing here decides
   * silently. A control that does nothing is the bug this codebase names.
   */
  async function closeYear(year: number) {
    setFiscalBusy(true);
    setFiscalError(null);
    setFiscalNotice(null);
    try {
      const result = await api.closeFiscalYear(year);
      setFiscalNotice(
        `Fiscal ${year} closed: ${formatCents(result.netIncomeCents)} into retained earnings`,
      );
      await loadFiscal();
    } catch (caught) {
      setFiscalError(caught instanceof ApiError ? caught.message : 'Could not close the year.');
    } finally {
      setFiscalBusy(false);
    }
  }

  async function reopenYear(year: number) {
    setFiscalBusy(true);
    setFiscalError(null);
    setFiscalNotice(null);
    try {
      await api.reopenFiscalYear(year, reopenReason);
      setFiscalNotice(`Fiscal ${year} reopened`);
      setReopeningYear(null);
      setReopenReason('');
      await loadFiscal();
    } catch (caught) {
      setFiscalError(caught instanceof ApiError ? caught.message : 'Could not reopen the year.');
    } finally {
      setFiscalBusy(false);
    }
  }

  useEffect(() => {
    let cancelled = false;
    api
      .getBooksLock()
      .then((result) => {
        if (!cancelled) setLock(result);
      })
      .catch((caught: unknown) => {
        if (!cancelled) {
          setLoadError(
            caught instanceof ApiError ? caught.message : 'Could not read the books settings.',
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    api
      .getPayrollSettings()
      .then((result) => {
        if (!cancelled) setPayroll(result);
      })
      .catch((caught: unknown) => {
        if (!cancelled) {
          setPayrollError(
            caught instanceof ApiError ? caught.message : 'Could not read the payroll settings.',
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /*
   * The preview follows the date field. A half-typed date asks nothing, and an
   * answer for a date the operator has since changed is dropped rather than
   * shown under the new one.
   */
  useEffect(() => {
    setPreview(null);
    if (!DATE.test(through)) return;
    let cancelled = false;
    api
      .getBooksLock(through)
      .then((result) => {
        if (!cancelled) setPreview(result.preview);
      })
      .catch((caught: unknown) => {
        if (!cancelled) {
          setError(caught instanceof ApiError ? caught.message : 'Could not preview that date.');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [through]);

  async function apply(lockedThrough: string | null) {
    setBusy(true);
    setError(null);
    try {
      const result = await api.setBooksLock(lockedThrough);
      setLock(result);
      setThrough('');
      setPreview(null);
      setConfirmingReopen(false);
      setNotice(
        result.lockedThrough === null
          ? 'Books reopened'
          : `Books closed through ${result.lockedThrough}`,
      );
      void loadHistory();
      // Each year's row states the server's obstacle, and the lock is one of
      // them (period_locked), so the years are read again too (review CB-1).
      void loadFiscal();
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : lockedThrough === null
            ? 'Could not reopen the books.'
            : 'Could not close the books.',
      );
    } finally {
      setBusy(false);
    }
  }

  /** §18.5 — the federal deposit schedule, as a dated history (migration 0048). */
  const loadSchedules = useCallback(async () => {
    try {
      const result = await api.listDepositSchedules();
      setSchedules(result.schedules);
    } catch (caught) {
      setScheduleError(caught instanceof ApiError ? caught.message : 'Could not read the deposit schedules.');
    }
  }, []);

  useEffect(() => {
    // Only for whoever sees the section (0069); anyone else would be refused.
    if (mayRunPayroll) void loadSchedules();
  }, [loadSchedules, mayRunPayroll]);

  async function addSchedule() {
    /*
     * Both refusals are sentences, and Save is always pressable: a button that
     * does nothing on an empty field is the silence CLAUDE.md warns about.
     */
    if (!DATE.test(scheduleFrom)) {
      setScheduleError('Enter the date the schedule takes effect.');
      return;
    }
    if (scheduleKind === '') {
      setScheduleError('Choose monthly or semiweekly.');
      return;
    }
    setPayrollBusy(true);
    setScheduleError(null);
    try {
      await api.recordDepositSchedule(scheduleFrom, scheduleKind);
      setPayrollNotice(`${scheduleKind === 'monthly' ? 'Monthly' : 'Semiweekly'} schedule recorded from ${scheduleFrom}`);
      setScheduleFrom('');
      setScheduleKind('');
      await loadSchedules();
    } catch (caught) {
      setScheduleError(caught instanceof ApiError ? caught.message : 'Could not record the schedule.');
    } finally {
      setPayrollBusy(false);
    }
  }

  async function removeSchedule(effectiveOn: string) {
    setPayrollBusy(true);
    setScheduleError(null);
    try {
      await api.removeDepositSchedule(effectiveOn);
      setRemovingSchedule(null);
      setPayrollNotice(`Schedule removed from ${effectiveOn}`);
      await loadSchedules();
    } catch (caught) {
      setRemovingSchedule(null);
      setScheduleError(caught instanceof ApiError ? caught.message : 'Could not remove the schedule.');
    } finally {
      setPayrollBusy(false);
    }
  }

  /** §18.2 — the rate the Texas Workforce Commission assigned for a year. */
  const loadTexasRates = useCallback(async () => {
    try {
      const result = await api.listTexasUnemploymentRates();
      setTexasRates(result.rates);
    } catch (caught) {
      setTexasError(
        caught instanceof ApiError ? caught.message : 'Could not read the Texas unemployment rates.',
      );
    }
  }, []);

  useEffect(() => {
    // Only for whoever sees the section (0069); anyone else would be refused.
    if (mayRunPayroll) void loadTexasRates();
  }, [loadTexasRates, mayRunPayroll]);

  async function addTexasRate() {
    const year = Number(texasYear);
    if (!Number.isInteger(year) || year < 2020) {
      setTexasError('A rate is for a calendar year from 2020 on.');
      return;
    }
    const millionths = parseRateMillionths(texasRate);
    if (millionths === null) {
      setTexasError('The rate is a percentage below 100%, to at most four decimal places.');
      return;
    }
    setPayrollBusy(true);
    setTexasError(null);
    try {
      await api.recordTexasUnemploymentRate(year, millionths);
      setTexasYear('');
      setTexasRate('');
      setPayrollNotice(`Texas unemployment rate recorded for ${year}`);
      await loadTexasRates();
    } catch (caught) {
      setTexasError(caught instanceof ApiError ? caught.message : 'Could not record the rate.');
    } finally {
      setPayrollBusy(false);
    }
  }

  async function removeTexasRate(year: number) {
    setPayrollBusy(true);
    setTexasError(null);
    try {
      await api.removeTexasUnemploymentRate(year);
      setRemovingTexasYear(null);
      setPayrollNotice(`Texas unemployment rate removed for ${year}`);
      await loadTexasRates();
    } catch (caught) {
      setRemovingTexasYear(null);
      setTexasError(caught instanceof ApiError ? caught.message : 'Could not remove the rate.');
    } finally {
      setPayrollBusy(false);
    }
  }

  async function changeAutoPost(autoPost: boolean) {
    setPayrollBusy(true);
    setPayrollError(null);
    try {
      const result = await api.setPayrollSettings(autoPost);
      setPayroll(result);
      setPayrollNotice(
        result.autoPost
          ? 'Payroll entries will be posted automatically'
          : 'Payroll entries will be saved as drafts',
      );
    } catch (caught) {
      setPayrollError(
        caught instanceof ApiError ? caught.message : 'Could not change the payroll setting.',
      );
    } finally {
      setPayrollBusy(false);
    }
  }

  if (loadError) {
    return (
      <section className="settings">
        <h1>Settings</h1>
        <p role="alert" className="error">
          {loadError}
        </p>
      </section>
    );
  }
  /*
   * Read through the hook, not by calling `displayTimeZone()` in the markup:
   * the preference lives outside React, so without a subscription the select
   * and the example below it would keep showing the old zone until something
   * else happened to re-render this screen. Above the early return, because a
   * hook cannot be called conditionally.
   */
  const chosenZone = useDisplayTimeZone();

  if (!lock) return <p className="muted">Loading…</p>;

  const zones = availableTimeZones();
  const zone = chosenZone;

  const stranded = preview ? preview.uncodedDraftCount + preview.codedDraftCount : 0;

  const offered = (
    [
      ['business', 'Business', true],
      ['banks', 'Banks', can('manage_banks') || can('edit_entries')],
      ['books', 'Books', true],
      ['payroll', 'Payroll', mayRunPayroll],
      ['security', 'Security', true],
      ['data', 'Your Data', mayExport || can('manage_users')],
    ] as Array<[SettingsTab, string, boolean]>
  ).filter(([, , shown]) => shown);
  const asked = searchParams.get('tab');
  const tab: SettingsTab = offered.find(([key]) => key === asked)?.[0] ?? 'business';

  return (
    <section className="settings">
      <h1>Settings</h1>

      <nav className="row tabs" aria-label="Settings">
        {offered.map(([key, label]) => (
          <button
            key={key}
            type="button"
            className={`tab${tab === key ? ' is-current' : ''}`}
            aria-current={tab === key}
            onClick={() => {
              const params = new URLSearchParams(searchParams);
              params.set('tab', key);
              setSearchParams(params, { replace: true });
            }}
          >
            {label}
          </button>
        ))}
      </nav>

      {tab === 'business' && (
        <>
          {can('manage_business') && <BusinessDetailsSection />}

          {/* A non-profit's funds (0079): read by everyone, kept by whoever manages the business. */}
          {usesFunds && <FundsSection />}

          {/* A partnership's partners and their shares (0096): read by everyone, kept by whoever manages the business. */}
          {partnership && <PartnersSection />}

          {/* Projects, a non-profit's programs (0081): read by everyone, kept by whoever manages the business. */}
          <ProjectsSection />

          {/* Phases (0091): the second tag, read by everyone, kept by whoever manages the business. */}
          <PhasesSection />

          {/* Labels by fiscal year (0090): read by everyone, kept by whoever edits entries. */}
          <LabelsSection />

          {/* Who is in this business and what each may do (plan §F.5.5): everyone sees it. */}
          <PeopleSection />
        </>
      )}

      {tab === 'banks' && (
        <>
          {/* Beside Business Details: where the business's bank feed comes from (issue #50). */}
          {can('manage_banks') && <BankConnectionsSection />}

          {/* ...and, for a bank with no feed, its downloaded files (plan §G.5). */}
          {can('edit_entries') && <BankImportSection />}
        </>
      )}

      {tab === 'books' && (
        <>
          {/*
            CLOSING THE BOOKS (issue #66): the two ways a period becomes fixed, in
            one section at the operator's request, kept as two actions. The year-end
            close (migration 0052) writes a real entry moving a year's revenue and
            expense into equity; the books lock fixes everything through a date and
            writes nothing. Close the year, then the books through its end, is the
            order -- a year inside the closed books cannot be closed -- so the
            year-end close comes first. Never one control doing both.
          */}
          {can('close_periods') && (
            <section className="settings-section" aria-labelledby="settings-closing">
              <h2 id="settings-closing">Closing The Books</h2>
              <p className="muted">
                Two separate steps. Closing a fiscal year moves its revenue and expense into equity as one entry and
                changes nothing else. Closing the books through a date fixes every entry on or before it. Close the
                fiscal year first, then close the books through its last day once its return is filed.
              </p>

              <section className="settings-subsection" aria-labelledby="settings-fiscal-years">
                <h3 id="settings-fiscal-years">Close A Fiscal Year</h3>

                {fiscal?.netAssets ? (
                  // A non-profit's year closes fund by fund into its net assets (N3).
                  <p className="muted">
                    Closing a year moves each fund&apos;s revenue and expense into its net assets, as one entry
                    dated the year&apos;s last day: into{' '}
                    {fiscal.netAssets.without ? `${fiscal.netAssets.without.code} ${fiscal.netAssets.without.name}` : 'net assets without donor restrictions'}{' '}
                    for General and other unrestricted funds, and into{' '}
                    {fiscal.netAssets.with ? `${fiscal.netAssets.with.code} ${fiscal.netAssets.with.name}` : 'net assets with donor restrictions'}{' '}
                    for restricted ones. Revenue and expense inside a closed year are then fixed; reopen the
                    year to change them.
                  </p>
                ) : partnership ? (
                  // 0096: a partnership's year is split across its partners' Capital accounts.
                  <p className="muted">
                    Closing a year moves its revenue and expense into each partner&apos;s Capital account, split by
                    the profit shares in force on the year&apos;s last day (the loss shares, for a loss), as one
                    entry dated that day. A year in which the shares changed is left for your CPA to split, so the
                    close refuses it. Revenue and expense inside a closed year are then fixed; reopen the year to
                    change them.
                  </p>
                ) : (
                  <p className="muted">
                    Closing a year moves its revenue and expense into{' '}
                    {fiscal?.retainedEarnings
                      ? `${fiscal.retainedEarnings.code} ${fiscal.retainedEarnings.name}`
                      : 'retained earnings'}{' '}
                    as one entry dated the year's last day, so the balance sheet shows that year in{' '}
                    {fiscal?.retainedEarnings ? fiscal.retainedEarnings.name : 'retained earnings'} and Current
                    earnings holds only the year in progress. Revenue and expense inside a closed year are then
                    fixed; reopen the year to change them.
                  </p>
                )}
                <InfoNote label="What the close leaves alone">
                  <p>
                    The income statement for a closed year is unchanged: a closing entry is a balance, not
                    activity, and the report leaves it out. The balance sheet counts it. The trial balance
                    dated on the year's last day leaves it out and says so, so the year's revenue and
                    expense still show; dated later, it counts it.
                  </p>
                  {partnership ? (
                    <p>
                      Partners&apos; draws are not closed. They stay on each partner&apos;s Draws account; if your
                      CPA wants them moved to Capital at year end, that is a manual entry.
                    </p>
                  ) : taxedWithOwner ? (
                    <p>
                      Owner&apos;s draws are not closed. They stay on their own account; if your CPA wants them
                      moved to Owner&apos;s Equity at year end, that is a manual entry.
                    </p>
                  ) : (
                    <p>
                      Shareholder distributions are not closed. The S-corp return reads them as their own
                      line; if your CPA wants them moved to retained earnings at year end, that is a manual
                      entry.
                    </p>
                  )}
                  <p>
                    Years close in order and reopen newest first. A draft dated inside a closed year can
                    still be edited or dismissed, but not posted until the year is reopened.
                  </p>
                </InfoNote>

                {fiscalNotice && !fiscalError && (
                  <Toast message={fiscalNotice} variant="ok" onDismiss={() => setFiscalNotice(null)} />
                )}
                {fiscalError && (
                  <p role="alert" className="error">
                    {fiscalError}
                  </p>
                )}

                {fiscal === null ? (
                  <p className="muted">Loading…</p>
                ) : fiscal.years.length === 0 ? (
                  <p className="muted">Nothing is posted yet, so there is no year to close.</p>
                ) : (
                  <table className="lines" aria-label="Fiscal years">
                    <thead>
                      <tr>
                        <th>Year</th>
                        <th>Ends</th>
                        <th className="num">Net Income</th>
                        <th>Status</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {fiscal.years.map((year) => {
                        const latestClosed =
                          year.close !== null &&
                          fiscal.years.every((other) => other.year <= year.year || other.close === null);
                        const drafts = year.uncodedDraftCount + year.codedDraftCount;
                        return (
                          <tr key={year.year}>
                            <td>{year.year}</td>
                            <td className="date-cell">{year.end}</td>
                            <td className="num">{formatCents(year.netIncomeCents)}</td>
                            <td>
                              {year.close !== null ? (
                                <>
                                  Closed {formatTimestamp(year.close.closedAt)}
                                  {year.close.closedByEmail ? ` · ${year.close.closedByEmail}` : ''}
                                  {/*
                                    The books' figure against the entry's. They differ only
                                    when something reached the year after it closed, which
                                    the freeze refuses and the lock makes rare -- so when
                                    they do, say so, or the row shows one number and the
                                    history another with nothing between them.
                                  */}
                                  {year.netIncomeCents !== year.close.netIncomeCents && (
                                    <>
                                      {' '}
                                      The books have changed since this year was closed; reopen it and
                                      close it again.
                                    </>
                                  )}
                                </>
                              ) : !year.ended ? (
                                'In progress'
                              ) : year.obstacle !== null ? (
                                year.obstacle
                              ) : drafts > 0 ? (
                                `Ready to close. ${[
                                  year.uncodedDraftCount > 0 && count(year.uncodedDraftCount, 'uncategorized draft', 'uncategorized drafts'),
                                  year.codedDraftCount > 0 && count(year.codedDraftCount, 'categorized draft', 'categorized drafts'),
                                ]
                                  .filter((part): part is string => typeof part === 'string')
                                  .join(' and ')} ${drafts === 1 ? 'is' : 'are'} dated inside it and cannot be posted once it is closed.`
                              ) : (
                                'Ready to close'
                              )}
                            </td>
                            <td className="line-actions">
                              {year.close === null && year.ended && (
                                <button
                                  type="button"
                                  className="link"
                                  disabled={fiscalBusy}
                                  onClick={() => void closeYear(year.year)}
                                >
                                  Close {year.year}
                                </button>
                              )}
                              {latestClosed && reopeningYear !== year.year && (
                                <button
                                  type="button"
                                  className="link"
                                  disabled={fiscalBusy}
                                  onClick={() => {
                                    setFiscalError(null);
                                    setReopeningYear(year.year);
                                  }}
                                >
                                  Reopen {year.year}
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}

                {reopeningYear !== null && (
                  <div className="new-account undo-form" role="group" aria-label={`Reopen ${reopeningYear}`}>
                    <p>
                      Reopen fiscal {reopeningYear}? Its closing entry is removed, its revenue and expense
                      become editable again, and the reopening is recorded with your reason.
                    </p>
                    <label className="field">
                      <span>Reason</span>
                      <input
                        value={reopenReason}
                        onChange={(event) => setReopenReason(event.target.value)}
                      />
                    </label>
                    <div className="row">
                      <button
                        type="button"
                        disabled={fiscalBusy}
                        onClick={() => void reopenYear(reopeningYear)}
                      >
                        Reopen It
                      </button>
                      <button
                        type="button"
                        className="link"
                        onClick={() => {
                          setReopeningYear(null);
                          setReopenReason('');
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {fiscal !== null && fiscal.history.length > 0 && (
                  <>
                    <h4>History</h4>
                    <ol className="history-list" aria-label="Fiscal year history">
                      {fiscal.history.map((change) => (
                        <li key={change.id}>
                          <p className="muted history-when">
                            {formatTimestamp(change.at)} · {change.actorEmail ?? 'No user recorded'}
                          </p>
                          <p>
                            {change.action === 'fiscal_year_closed'
                              ? `Closed fiscal ${change.year}${change.netIncomeCents === null ? '' : `, ${formatCents(change.netIncomeCents)} into retained earnings`}`
                              : `Reopened fiscal ${change.year}${change.reason ? `: ${change.reason}` : ''}`}
                          </p>
                        </li>
                      ))}
                    </ol>
                  </>
                )}
              </section>

              <section className="settings-subsection" aria-labelledby="settings-books">
                <h3 id="settings-books">Close The Books Through A Date</h3>

                <p role="status" className={`notice ${lock.lockedThrough ? 'notice-posted' : ''}`}>
                  {lock.lockedThrough ? `Closed through ${lock.lockedThrough}` : 'Books are open'}
                </p>

                <p className="muted">
                  Closing the books fixes every entry dated on or before the date you choose: it can no
                  longer be edited, deleted or posted. Close them through a year&apos;s end after its
                  return is filed, not on December 31, so the adjustments that arrive in the months after
                  still have somewhere to go.
                </p>

                {notice && !error && (
                  <Toast message={notice} variant="ok" onDismiss={() => setNotice(null)} />
                )}
                {error && (
                  <p role="alert" className="error">
                    {error}
                  </p>
                )}

                <label className="field settings-date">
                  <span>Close Through</span>
                  <input
                    type="date"
                    value={through}
                    max={dayBefore(lock.today)}
                    onChange={(event) => {
                      setError(null);
                      setThrough(event.target.value);
                    }}
                  />
                </label>

                {preview && (
                  <div className="books-preview">
                    <p>
                      {count(preview.postedEntryCount, 'posted entry', 'posted entries')} dated on or
                      before {preview.through} would become fixed.
                    </p>
                    {stranded > 0 && (
                      <p className="notice notice-warn">
                        {count(preview.uncodedDraftCount, 'uncategorized draft', 'uncategorized drafts')} and{' '}
                        {count(preview.codedDraftCount, 'categorized draft', 'categorized drafts')} are dated on or
                        before {preview.through}. Once the books are closed they can never be posted.
                      </p>
                    )}
                    <button type="button" disabled={busy} onClick={() => void apply(through)}>
                      Close the Books
                    </button>
                  </div>
                )}

                {lock.lockedThrough && !confirmingReopen && (
                  <p>
                    <button type="button" className="link" onClick={() => setConfirmingReopen(true)}>
                      Reopen the Books
                    </button>
                  </p>
                )}
                {lock.lockedThrough && confirmingReopen && (
                  <div className="new-account undo-form" role="group" aria-label="Reopen the Books">
                    <p>
                      Reopen the books? Every entry dated through {lock.lockedThrough} becomes editable
                      again. The reopening is recorded.
                    </p>
                    <div className="row">
                      <button type="button" disabled={busy} onClick={() => void apply(null)}>
                        Reopen Them
                      </button>
                      <button type="button" className="link" onClick={() => setConfirmingReopen(false)}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                <h4>History</h4>
                {historyError ? (
                  <p role="alert" className="error">
                    {historyError}
                  </p>
                ) : history === null ? (
                  <p className="muted">Loading…</p>
                ) : history.length === 0 ? (
                  <p className="muted">No changes recorded.</p>
                ) : (
                  <ol className="history-list" aria-label="Books history">
                    {history.map((change) => (
                      <li key={change.id}>
                        <p className="muted history-when">
                          {formatTimestamp(change.at)} · {change.actorEmail ?? 'No user recorded'}
                        </p>
                        <p>{describeLockChange(change)}</p>
                      </li>
                    ))}
                  </ol>
                )}
              </section>
            </section>
          )}

          {/*
            §5.4 — WHICH ZONE TIMESTAMPS ARE SHOWN IN, and nothing else.

            THIS IS NOT THE BUSINESS TIMEZONE. `tenants.business_timezone` decides
            which calendar DAY a transaction belongs to: a payment at 7pm on 31
            December in America/Chicago is a December transaction, and the server
            resolves that before anything is stored. Changing it would move entries
            between periods, retroactively, including posted ones in a closed year.
            It is deliberately not editable here.

            What this changes is how an INSTANT is drawn — when an entry was
            posted, when a sync ran. Those are the same moment everywhere, and
            showing them where the reader is has no accounting consequence. Entry
            dates are untouched: a DATE has no time in it, and putting one through a
            zone conversion is how 2026-08-22 becomes 2026-08-21.

            Per browser, not per tenant. It answers "what time is it where I am",
            which is a property of the reader, and it is right before anybody opens
            this screen because it starts from the browser's own zone.
          */}
          <section className="settings-section" aria-labelledby="settings-time">
            <h2 id="settings-time">Time Zone</h2>

            <p className="muted">
              Timestamps are shown in this zone. It does not change which day an
              entry belongs to — that is the business time zone, which is set when
              the books are created and is not editable here.
            </p>

            <label className="settings-date">
              <span>Show Times In</span>
              <select
                aria-label="Display time zone"
                value={zone}
                onChange={(event) => setDisplayTimeZone(event.target.value)}
              >
                {zones.map((option) => (
                  <option key={option} value={option}>
                    {option === browserTimeZone() ? `${option} (this browser)` : option}
                  </option>
                ))}
              </select>
            </label>

            {/*
              Shown, not described. A zone name is not something most people can
              convert in their head, and the whole question the operator has is
              "what will a timestamp look like" -- so the answer is a timestamp.
            */}
            <p className="muted">
              Right now that reads <strong>{formatTimestamp(new Date().toISOString())}</strong>.
            </p>

            {zone !== browserTimeZone() && (
              <button type="button" className="link" onClick={() => clearDisplayTimeZone()}>
                Use This Browser's Zone ({browserTimeZone()})
              </button>
            )}
          </section>
        </>
      )}

      {tab === 'payroll' && (
        <>
          {can('run_payroll') && (
            <section className="settings-section" aria-labelledby="settings-payroll">
              <h2 id="settings-payroll">Payroll</h2>

              {payrollNotice && !payrollError && (
                <Toast message={payrollNotice} variant="ok" onDismiss={() => setPayrollNotice(null)} />
              )}
              {payrollError && (
                <p role="alert" className="error">
                  {payrollError}
                </p>
              )}

              {payroll === null ? (
                !payrollError && <p className="muted">Loading…</p>
              ) : (
                <>
                  <label className="toggle-line">
                    <input
                      type="checkbox"
                      checked={payroll.autoPost}
                      disabled={payrollBusy}
                      onChange={(event) => void changeAutoPost(event.target.checked)}
                    />{' '}
                    Post Payroll Entries Automatically
                  </label>
                  <p className="muted">
                    {payroll.autoPost
                      ? 'Each payroll run will be posted as it is made, through the same checks as posting by hand: a closed period, or an entry that does not balance, is refused and shown.'
                      : 'Each payroll run will be saved as a draft for you to review and post.'}
                  </p>
                </>
              )}

              <h3>Federal Deposit Schedule</h3>
              <p className="muted">
                Monthly or semiweekly, as the IRS assigns it for each calendar year. A pay's federal deposit is due under
                the schedule in effect on its pay date, so recording a new one never moves a deposit already dated.{' '}
                <InfoNote label="How the IRS decides the schedule">
                  From the Form 941 taxes reported in the four quarters from July two years before through June of the
                  year before: $50,000 or less is monthly, more is semiweekly. A new employer is monthly for its first
                  year. A liability of $100,000 or more on any one day makes a monthly depositor semiweekly from the next
                  day, for the rest of that year and all of the next (Publication 15, section 11).
                </InfoNote>
              </p>
              {scheduleError && (
                <p role="alert" className="error">
                  {scheduleError}
                </p>
              )}
              <div className="new-account" role="group" aria-label="Record a Deposit Schedule">
                <div className="field-row">
                  <label className="field field-medium">
                    <span>Takes Effect</span>
                    <input
                      type="date"
                      value={scheduleFrom}
                      onChange={(event) => {
                        const value = event.target.value;
                        setScheduleFrom(value);
                      }}
                    />
                  </label>
                  <label className="field field-medium">
                    <span>Schedule</span>
                    <select
                      value={scheduleKind}
                      onChange={(event) => {
                        const value = event.target.value as DepositSchedule | '';
                        setScheduleKind(value);
                      }}
                    >
                      <option value="">Choose…</option>
                      <option value="monthly">Monthly</option>
                      <option value="semiweekly">Semiweekly</option>
                    </select>
                  </label>
                </div>
                <div className="row">
                  <button type="button" disabled={payrollBusy} onClick={() => void addSchedule()}>
                    Save Schedule
                  </button>
                </div>
              </div>
              {schedules === null ? (
                !scheduleError && <p className="muted">Loading…</p>
              ) : schedules.length === 0 ? (
                <p className="muted">No deposit schedule on file, so no federal deposit has a date yet.</p>
              ) : (
                <ol className="history-list" aria-label="Deposit Schedules">
                  {schedules.map((entry) => (
                    <li key={entry.effectiveOn}>
                      <p>
                        <strong>
                          {entry.schedule === 'monthly' ? 'Monthly' : 'Semiweekly'} from {entry.effectiveOn}
                        </strong>
                      </p>
                      <p className="muted history-when">recorded by {entry.recordedByEmail ?? 'no user recorded'}</p>
                      {removingSchedule === entry.effectiveOn ? (
                        <div className="new-account undo-form" role="group" aria-label="Remove This Schedule">
                          <p>
                            Remove the schedule taking effect {entry.effectiveOn}? The deposits it dated fall back on the
                            schedule before it, or have no date.
                          </p>
                          <div className="row">
                            <button
                              type="button"
                              disabled={payrollBusy}
                              onClick={() => void removeSchedule(entry.effectiveOn)}
                            >
                              Remove It
                            </button>
                            <button type="button" className="link" onClick={() => setRemovingSchedule(null)}>
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="link"
                          aria-label={`Remove the schedule taking effect ${entry.effectiveOn}`}
                          onClick={() => setRemovingSchedule(entry.effectiveOn)}
                        >
                          Remove
                        </button>
                      )}
                    </li>
                  ))}
                </ol>
              )}

              <h3>Texas Unemployment Rate</h3>
              <p className="muted">
                The rate the Texas Workforce Commission assigned this business for a year, from its notice.
                A run in a year needs the year's rate, and a rate a run used is kept.
              </p>
              {texasError && (
                <p role="alert" className="error">
                  {texasError}
                </p>
              )}
              <div className="new-account" role="group" aria-label="Record a Texas Unemployment Rate">
                <div className="field-row">
                  <label className="field field-short">
                    <span>Year</span>
                    <input
                      inputMode="numeric"
                      value={texasYear}
                      onChange={(event) => setTexasYear(event.target.value)}
                    />
                  </label>
                  <label className="field field-short">
                    <span>Rate</span>
                    <input
                      inputMode="decimal"
                      placeholder="2.7"
                      value={texasRate}
                      onChange={(event) => setTexasRate(event.target.value)}
                    />
                  </label>
                </div>
                <div className="row">
                  <button type="button" disabled={payrollBusy} onClick={() => void addTexasRate()}>
                    Save Rate
                  </button>
                </div>
              </div>
              {texasRates === null ? (
                <p className="muted">Loading…</p>
              ) : texasRates.length === 0 ? (
                <p className="muted">No Texas unemployment rate on file.</p>
              ) : (
                <ol className="history-list" aria-label="Texas Unemployment Rates">
                  {texasRates.map((rate) => (
                    <li key={rate.year}>
                      <p>
                        <strong>
                          {rate.year}: {rate.rate}
                        </strong>
                      </p>
                      <p className="muted history-when">
                        recorded by {rate.recordedByEmail ?? 'no user recorded'}
                      </p>
                      {removingTexasYear === rate.year ? (
                        <div className="new-account undo-form" role="group" aria-label="Remove This Rate">
                          <p>Remove the {rate.year} rate? A rate a payroll run used is kept instead.</p>
                          <div className="row">
                            <button type="button" disabled={payrollBusy} onClick={() => void removeTexasRate(rate.year)}>
                              Remove It
                            </button>
                            <button type="button" className="link" onClick={() => setRemovingTexasYear(null)}>
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="link"
                          aria-label={`Remove the ${rate.year} Texas unemployment rate`}
                          onClick={() => setRemovingTexasYear(rate.year)}
                        >
                          Remove
                        </button>
                      )}
                    </li>
                  ))}
                </ol>
              )}
            </section>
          )}
        </>
      )}

      {tab === 'security' && (
        <>
          <section className="settings-section" aria-labelledby="settings-signin">
            <h2 id="settings-signin">Signing In</h2>

            {securityError && (
              <p role="alert" className="error">
                {securityError}
              </p>
            )}

            {issuedCodes !== null ? (
              <RecoveryCodes
                codes={issuedCodes}
                reason={issuedReason}
                onDone={() => {
                  setIssuedCodes(null);
                  void loadSecurity();
                }}
              />
            ) : (
              <>
                {security === null ? (
                  !securityError && <p className="muted">Loading…</p>
                ) : (
                  <>
                    <p className="muted">
                      {security.totpEnrolledAt
                        ? `Authenticator enrolled ${formatTimestamp(security.totpEnrolledAt)}.`
                        : 'No authenticator is enrolled on this account.'}
                    </p>

                    {/*
                      * The count, said plainly and never hidden at zero. A recovery
                      * sign-in spends one and nothing else replaces them, so this
                      * is a countdown until somebody reissues.
                      */}
                    <p className={security.remainingRecoveryCodes === 0 ? 'error' : 'muted'}>
                      {security.remainingRecoveryCodes === 0
                        ? 'No recovery codes left. If you lose your authenticator now, this account cannot be reached.'
                        : `${count(security.remainingRecoveryCodes, 'recovery code', 'recovery codes')} left.`}
                    </p>
                  </>
                )}

                {securityStep === 'idle' && (
                  <div className="row">
                    <button
                      type="button"
                      disabled={securityBusy}
                      onClick={() => {
                        setSecurityStep('reissue');
                        setSecurityCode('');
                        setSecurityError(null);
                      }}
                    >
                      Reissue Recovery Codes
                    </button>
                    <button type="button" disabled={securityBusy} onClick={() => void startEnrolment()}>
                      Move To A New Authenticator
                    </button>
                  </div>
                )}

                {securityStep === 'reissue' && (
                  <div className="new-account undo-form authenticator-form" role="group" aria-label="Reissue Recovery Codes">
                    <p>
                      Ten new codes, replacing the ones you have. Enter a code from your
                      authenticator, or one of your existing recovery codes if the authenticator
                      is what went missing.
                    </p>
                    <label className="field">
                      <span>Authenticator Or Recovery Code</span>
                      <input
                        value={securityCode}
                        autoComplete="one-time-code"
                        onChange={(event) => setSecurityCode(event.target.value)}
                      />
                    </label>
                    <div className="row">
                      <button type="button" disabled={securityBusy} onClick={() => void reissueCodes()}>
                        {securityBusy ? 'Checking…' : 'Reissue Them'}
                      </button>
                      <button type="button" className="link" onClick={resetSecurity}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {securityStep === 'enrol' && enrolment !== null && (
                  <div className="new-account undo-form authenticator-form" role="group" aria-label="Move To A New Authenticator">
                    <p>
                      Add this account to your new authenticator, then enter the code it shows.
                      <strong> Nothing changes until you do</strong> — your current authenticator
                      keeps working until then.
                    </p>
                    <p className="muted">
                      On this device, open{' '}
                      <a href={enrolment.otpauthUri}>the authenticator link</a>. On another device,
                      add it by hand with this key:
                    </p>
                    <p>
                      <code className="enrolment-secret">{enrolment.secret}</code>
                    </p>
                    <label className="field">
                      <span>Code From The New Device</span>
                      <input
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        value={securityCode}
                        onChange={(event) => setSecurityCode(event.target.value)}
                      />
                    </label>
                    {/*
                      * THE FACTOR BEING REPLACED, and the move is refused without
                      * it. A live session alone must not be able to swap somebody's
                      * authenticator and delete their recovery codes -- a borrowed
                      * laptop would otherwise be a lasting takeover.
                      */}
                    <label className="field">
                      <span>Code From Your Current Authenticator</span>
                      <input
                        autoComplete="one-time-code"
                        value={currentFactor}
                        onChange={(event) => setCurrentFactor(event.target.value)}
                      />
                    </label>
                    <p className="muted">
                      Lost the old device? Use one of your recovery codes here instead.
                    </p>
                    <div className="row">
                      <button type="button" disabled={securityBusy} onClick={() => void finishEnrolment()}>
                        {securityBusy ? 'Checking…' : 'Finish The Move'}
                      </button>
                      <button type="button" className="link" onClick={resetSecurity}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </section>

          {/* Beside Signing In: a link is a fact about this sign-in (issue #50). */}
          <LinkedSignInsSection />
        </>
      )}

      {tab === 'data' && (
        <>
          {/* N7 (§B.11.1.5): a new business's books from QuickBooks, an admin's, once. */}
          {can('manage_business') && can('edit_entries') && can('close_periods') && <QuickBooksImportSection />}

          {can('export_data') && (
            <section className="settings-section" aria-labelledby="settings-data">
              <h2 id="settings-data">Your Data</h2>

              <p className="muted">
                An archive holds everything this business has in here: the ledger and every
                entry behind it, the chart of accounts, parties, reconciliations, payroll, and
                the original PDF of every receipt and bank statement. It is yours to keep, and
                it opens with ordinary software — the spreadsheet and the CSVs need nothing
                from us. How Techne Books collects, keeps and protects it is in the{' '}
                <a href="/privacy">Privacy Policy</a>.
              </p>
              <p className="muted">
                Tax identification numbers and Social Security numbers are in it but{' '}
                <strong>encrypted</strong>, and the key is not in the file, so nobody can read
                them from it alone. Passwords, two-factor secrets and recovery codes are not in
                it at all — <strong>an archive cannot sign you in</strong>, so it is not a way
                back into an account you are locked out of. Keep more than one, somewhere you
                control.
              </p>

              {exportNotice && !exportError && (
                <Toast message={exportNotice} variant="ok" onDismiss={() => setExportNotice(null)} />
              )}
              {exportError && (
                <p role="alert" className="error">
                  {exportError}
                </p>
              )}

              <button type="button" disabled={exportBusy} onClick={() => void prepareArchive()}>
                {exportBusy ? 'Starting…' : 'Prepare An Archive'}
              </button>

              {exports === null ? (
                !exportError && <p className="muted">Loading…</p>
              ) : exports.length === 0 ? (
                <p className="muted">No archive has been prepared yet.</p>
              ) : (
                <ol className="history-list" aria-label="Archives">
                  {exports.map((archive) => (
                    <li key={archive.id}>
                      <p>{describeExport(archive)}</p>
                      <p className="muted history-when">
                        asked for {formatTimestamp(archive.requestedAt)}
                      </p>
                      {archive.downloadable && (
                        <button
                          type="button"
                          className="link"
                          aria-label={`Download the archive from ${formatTimestamp(archive.requestedAt)}`}
                          onClick={() => void downloadArchive(archive.id)}
                        >
                          Download
                        </button>
                      )}
                    </li>
                  ))}
                </ol>
              )}
            </section>
          )}

          {/* Last, after Your Data: an archive is prepared before closing (C-18). */}
          <CloseBusinessSection />
        </>
      )}
    </section>
  );
}

/** How big it is, in a unit a person reads. */
/**
 * How often the screen looks again while an archive is building.
 *
 * Five seconds because a build takes tens of seconds to a couple of minutes,
 * and the person is watching the row they just created. It is not a background
 * poll: it only runs while something is in progress, and stops on its own.
 */
const EXPORT_POLL_MS = 5_000;

export function describeSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} bytes`;
  const units = ['KB', 'MB', 'GB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value < 10 ? value.toFixed(1) : Math.round(value)} ${units[unit]}`;
}

/**
 * One archive, in a sentence.
 *
 * STALE IS NOT "PREPARING". A row nothing will finish must not be shown as in
 * progress: a progress state that never resolves is how somebody concludes the
 * feature is broken and stops trying. It says what happened and what to do.
 */
export function describeExport(archive: TenantExport): string {
  if (archive.status === 'running') {
    return archive.stale
      ? 'Abandoned — the server restarted while it was being prepared. Prepare another.'
      : 'Preparing…';
  }
  if (archive.status === 'failed') {
    return `Failed — ${archive.errorMessage ?? 'no reason was recorded'}`;
  }
  if (archive.status === 'expired') {
    return 'Replaced by newer archives; its file has been deleted.';
  }
  const entries = archive.rowCounts?.journal_headers;
  const size = archive.sizeBytes === null ? null : describeSize(archive.sizeBytes);
  return [
    'Ready',
    size,
    entries === undefined ? null : count(entries, 'entry', 'entries'),
  ]
    .filter((part): part is string => part !== null)
    .join(' — ');
}

/** One change to the lock date, in words. The direction is the trigger's. */
export function describeLockChange(change: BooksLockChange): string {
  if (change.action === 'books_closed') {
    return change.from === null
      ? `Closed the books through ${change.to}`
      : `Moved the close date forward from ${change.from} to ${change.to}`;
  }
  return change.to === null
    ? `Reopened the books, which were closed through ${change.from}`
    : `Moved the close date back from ${change.from} to ${change.to}, reopening the days between`;
}
